import {HttpClient} from '@angular/common/http';

/* eslint-disable quote-props */
import {Injectable} from '@angular/core';
import {FeatureCollection, LineString, Point} from 'geojson';
import {Observable, of} from 'rxjs';
// @ts-ignore
import {distinctUntilChanged, shareReplay, take} from 'rxjs/operators';
import {Response} from '@wm-types/elastic';
import {Filter, SliderFilter} from '../../../types/config';
import {synchronizedApi, getEcTrack as getEcTrackFromLocalForage, saveEcTrack} from '@wm-core/utils/localForage';
import {WmFeature} from '@wm-types/feature';
import {EnvironmentService} from '@wm-core/services/environment.service';
import {LangService} from '@wm-core/localization/lang.service';
import {EC_NOT_PASSABLE_ONLY_LABEL, EC_NOT_PASSABLE_SUFFIX} from '@wm-core/constants/ec';
@Injectable({
  providedIn: 'root',
})
export class EcService {
  private static readonly NOT_ACCESSIBLE_RELATED_POI_ID = -999999;

  private _queryDic: {[query: string]: any} = {};
  private _shard = 'geohub_app';

  private get _baseUrl(): string {
    const appId = this._environmentSvc.appId;
    return appId
      ? `${this._environmentSvc.elasticApi}/?app=${this._shard}_${appId}`
      : this._environmentSvc.elasticApi;
  }

  /**
   * Creates an instance of ElasticService.
   * @param {HttpClient} _http
   * @memberof ElasticService
   */
  constructor(private _http: HttpClient, private _environmentSvc: EnvironmentService, private _langSvc: LangService) {}

  public decorateNotAccessibleRelatedPoi(
    track: WmFeature<LineString> | null | undefined,
  ): WmFeature<LineString> | null | undefined {
    return this._ensureNotAccessibleRelatedPoi(track);
  }

  public getEcTrack(id: string | number): Observable<WmFeature<LineString>> {
    if (id == null) return of(null);
    if (+id > -1) {
      const url = `${this._environmentSvc.awsApi}/tracks/${id}.json`;
      const lastModifiedKey = `${url}-last-modified`;

      return new Observable<WmFeature<LineString>>(observer => {
        // Prima cerca la track nel localForage (ec-tracks synchronized)
        getEcTrackFromLocalForage(`${id}`).then(cachedTrack => {
          // Se ci sono dati in cache locale, emettili subito
          if (cachedTrack) {
            observer.next(cachedTrack);
          }

          // Effettua la richiesta HTTP per ottenere la versione aggiornata
          this._http
            .get<WmFeature<LineString>>(url, {
              observe: 'response',
              headers: (() => {
                const cachedLastModified = localStorage.getItem(lastModifiedKey);
                return cachedTrack && cachedLastModified ? {'If-Modified-Since': cachedLastModified} : {};
              })(),
            })
            .pipe(take(1))
            .subscribe(
              response => {
                const lastModified = response.headers.get('last-modified');

                if (response.status === 200) {
                  const remoteTrack = response.body;

                  if (remoteTrack) {
                    // Aggiorna il localForage solo se la track era già stata scaricata dall'utente
                    if (cachedTrack) {
                      saveEcTrack(`${id}`, remoteTrack).catch(err =>
                        console.warn('getEcTrack: Failed to update localForage cache', err),
                      );
                    }

                    if (lastModified) {
                      localStorage.setItem(lastModifiedKey, lastModified);
                    }

                    observer.next(remoteTrack);
                  }
                } else if (response.status === 304) {
                  console.log(`No changes detected for track ${id}, using cached data.`);
                }

                observer.complete();
              },
              error => {
                if (!cachedTrack) {
                  observer.error(error); // Errore solo se non ci sono dati in cache
                } else {
                  observer.complete(); // Completa senza errore se esiste la cache
                }
              },
            );
        });
      });
    }
  }

  public getPois(): Observable<FeatureCollection> {
    const poisUrl = this._environmentSvc.awsPoisUrl;

    return new Observable<FeatureCollection>(observer => {
      synchronizedApi.getItem(`${poisUrl}`).then((cachedData: string | null) => {
        let parsedData: FeatureCollection | null = null;
        const cachedLastModified = localStorage.getItem(`${poisUrl}-last-modified`);

        // Se ci sono dati in cache, invia immediatamente
        if (cachedData) {
          try {
            parsedData = JSON.parse(cachedData) as FeatureCollection;
            observer.next(parsedData);
          } catch (e) {
            console.warn('Error parsing cached data. Ignoring cached data.', e);
          }
        }

        // Effettua la richiesta HTTP con Last-Modified
        this._http
          .get<FeatureCollection>(poisUrl, {
            observe: 'response',
            headers: cachedLastModified ? {'If-Modified-Since': cachedLastModified} : {},
          })
          .pipe(take(1))
          .subscribe(
            response => {
              const lastModified = response.headers.get('last-modified');

              if (response.status === 200) {
                const pois = response.body;

                if (pois) {
                  // Aggiorna la cache solo con dati nuovi
                  synchronizedApi.setItem(`${poisUrl}`, JSON.stringify(pois));
                  if (lastModified) {
                    localStorage.setItem(`${poisUrl}-last-modified`, lastModified);
                  }
                  observer.next(pois);
                }
              } else if (response.status === 304) {
                console.log('No changes detected for pois, using cached data.');
              }

              observer.complete();
            },
            error => {
              if (!parsedData) {
                observer.error(error); // Errore se non ci sono dati in cache
              } else {
                observer.complete(); // Completa senza errore se esiste la cache
              }
            },
          );
      });
    }).pipe(
      distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)),
      shareReplay(1), // Condivide la risposta tra più osservatori
    );
  }

  /**
   * @description
   * This function is called getQuery and takes two optional parameters,
   * inputTyped and layer. It returns an Observable of type IELASTIC.
   * It builds a query string using the baseUrl and the two optional parameters
   * if they are provided. It then makes a GET request to the built query string and returns
   * the result as an Observable of type IELASTIC.
   *
   * @param {string} [inputTyped]
   * @param {number} [layer]
   * @returns {*}  {Observable<IELASTIC>}
   * @memberof ElasticService
   */
  async getQuery(options: {
    inputTyped?: string;
    layer?: any;
    filterTracks?: Filter[];
    trackIds?: number[];
  }): Promise<Response> {
    await this._environmentSvc.readyPromise;
    let query = this._baseUrl;

    if (options.inputTyped) {
      query += `&query=${options.inputTyped.replace(/ /g, '%20')}`;
    }

    if (options.layer && options.layer.id != null) {
      query += `&layer=${options.layer.id}`;
    }

    if (options.trackIds) {
      query += `&ids=[${options.trackIds.join(',')}]`;
    }

    if (options.filterTracks != null && options.filterTracks.length > 0) {
      const paramString = options.filterTracks.map(filterTrack => {
        if (filterTrack.type === 'slider') {
          const sliderFilter = filterTrack as unknown as SliderFilter;
          return JSON.stringify({
            identifier: sliderFilter.identifier,
            min: sliderFilter.lower,
            max: sliderFilter.upper,
          });
        } else {
          return JSON.stringify({
            identifier: filterTrack.identifier,
            taxonomy: filterTrack.taxonomy,
          });
        }
      });

      query += `&filters=[${paramString.toString()}]`;
    }
    if (this._queryDic[query] == null && query != null) {
      const value = await this._http.request('get', query).toPromise();
      this._queryDic[query] = value;
    }
    return this._queryDic[query];
  }

  private _pickFirstStringValue(value: unknown): string | null {
    if (typeof value === 'string') return value;
    if (value && typeof value === 'object') {
      for (const v of Object.values(value as Record<string, unknown>)) {
        if (typeof v === 'string' && v.trim().length > 0) return v;
      }
    }
    return null;
  }

  private _trackNameForLang(
    rawName: unknown,
    lang: string,
    defaultLang: string,
  ): string {
    if (rawName && typeof rawName === 'object' && !Array.isArray(rawName)) {
      const o = rawName as Record<string, unknown>;
      const direct = o[lang];
      if (typeof direct === 'string' && direct.trim().length > 0) {
        return direct.trim();
      }
      const fromDefault = o[defaultLang];
      if (typeof fromDefault === 'string' && fromDefault.trim().length > 0) {
        return fromDefault.trim();
      }
      return this._pickFirstStringValue(rawName)?.trim() ?? '';
    }
    if (typeof rawName === 'string' && rawName.trim().length > 0) {
      return rawName.trim();
    }
    return '';
  }

  private _notPassableRelatedPoiName(rawName: unknown): Record<string, string> {
    const defaultLang = this._langSvc.defaultLang || 'it';
    const langs = [...(this._langSvc.getLangs() ?? [])];
    if (langs.length === 0) {
      const cur = this._langSvc.currentLang;
      if (cur) {
        langs.push(cur);
      }
      const fb = this._langSvc.defaultLang;
      if (fb && !langs.includes(fb)) {
        langs.push(fb);
      }
    }
    const out: Record<string, string> = {};
    for (const lang of langs) {
      const base = this._trackNameForLang(rawName, lang, defaultLang);
      const suffix = EC_NOT_PASSABLE_SUFFIX[lang] ?? EC_NOT_PASSABLE_SUFFIX['en'];
      const only = EC_NOT_PASSABLE_ONLY_LABEL[lang] ?? EC_NOT_PASSABLE_ONLY_LABEL['en'];
      out[lang] = base.length > 0 ? `${base} ${suffix}` : only;
    }
    return out;
  }

  private _getLineStringMidpoint(
    track: WmFeature<LineString> | null | undefined,
  ): [number, number] | null {
    const coords = (track as any)?.geometry?.coordinates;
    if (!Array.isArray(coords) || coords.length === 0) return null;
    const mid = coords[Math.floor(coords.length / 2)];
    if (!Array.isArray(mid) || mid.length < 2) return null;
    const lon = Number(mid[0]);
    const lat = Number(mid[1]);
    if (!Number.isFinite(lon) || !Number.isFinite(lat)) return null;
    return [lon, lat];
  }

  private _ensureNotAccessibleRelatedPoi(
    track: WmFeature<LineString> | null | undefined,
  ): WmFeature<LineString> | null | undefined {
    if (track == null) return track;
    const props = (track as any).properties ?? null;
    if (props?.not_accessible !== true) return track;

    const coordinates = this._getLineStringMidpoint(track);
    if (coordinates == null) return track;

    const existing = (props.related_pois as any[]) ?? [];
    const alreadyThere = existing.some(
      p => +p?.properties?.id === EcService.NOT_ACCESSIBLE_RELATED_POI_ID,
    );
    if (alreadyThere) return track;

    const message = props?.not_accessible_message ?? '';
    const name = this._notPassableRelatedPoiName(props?.name);

    const poi: WmFeature<Point> = {
      type: 'Feature',
      properties: {
        id: EcService.NOT_ACCESSIBLE_RELATED_POI_ID,
        wmNotAccessibleRelated: true,
        name,
        description: message,
        feature_image: {
          sizes: {
            '108x137': '/assets/icon/not-accessible.png',
            '108x148': '/assets/icon/not-accessible.png',
          },
        },
        related_url: null,
      } as any,
      geometry: {
        type: 'Point',
        coordinates,
      },
    } as any;

    return {
      ...(track as any),
      properties: {
        ...props,
        related_pois: [...existing, poi],
      },
    };
  }
}

export const hostToGeohubAppId: {[key: string]: number} = {
  'sentieri.caiparma.it': 33,
  'motomappa.motoabbigliamento.it': 53,
  'maps.parcoforestecasentinesi.it': 49,
  'maps.parcopan.org': 63,
  'maps.acquasorgente.cai.it': 58,
  'maps.caipontedera.it': 59,
  'maps.parcapuane.it': 62,
  'fiemaps.it': 29,
  'fiemaps.eu': 29,
};
